import React, { useEffect, useMemo, useState } from 'react';
import { Globe } from 'lucide-react';
import './MarketplaceManagement.css';

const DEFAULT_CONFIG = {
  isEnabled: true,
  featuredMerchantSlugs: [],
  featuredProductIds: [],
  sliderMerchantSlugs: [],
  merchantProductLimit: 5,
  tierProductLimits: {
    FREE: 4,
    PREMIUM: 5,
    PRO: 5,
    'PRO PLUS': 5
  },
  sections: {
    featuredMerchants: true,
    featuredProducts: true,
    allMerchants: true
  },
  hero: {
    eyebrow: 'Revstar marketplace',
    title: 'Discover standout stores and products from Revstar merchants.',
    description: 'Browse featured merchants, inspect their best products, and jump straight into their live storefronts.',
    primaryCtaLabel: 'Browse merchants',
    secondaryCtaLabel: 'Explore products'
  },
  sponsoredProducts: {
    enabled: true,
    slots: 6,
    rotationHours: 6,
    fallbackToFeaturedProducts: true,
    directoryOnlyWithoutBoosts: true,
    queuePolicy: 'FIFO',
    reportNote: 'Reports cover visibility on the Revstar marketplace surface only. Boost improves discovery, not guaranteed sales.',
    tierPricing: {
      FREE: { discountPercent: 0, priorityWeight: 1 },
      PREMIUM: { discountPercent: 10, priorityWeight: 2 },
      PRO: { discountPercent: 15, priorityWeight: 3 },
      'PRO PLUS': { discountPercent: 20, priorityWeight: 4 }
    },
    defaultPackage: {
      name: '7-day sponsored slot',
      durationDays: 7,
      price: 5000
    }
  },
  boostCampaigns: []
};

const DEFAULT_COMPOSER = {
  productId: '',
  packageName: DEFAULT_CONFIG.sponsoredProducts.defaultPackage.name,
  durationDays: DEFAULT_CONFIG.sponsoredProducts.defaultPackage.durationDays,
  price: DEFAULT_CONFIG.sponsoredProducts.defaultPackage.price,
  notes: '',
  startMode: 'queue'
};

function uniqueStrings(items = []) {
  return Array.from(new Set(items.filter(Boolean)));
}

function moveArrayItem(items = [], fromIndex, direction) {
  const next = [...items];
  const toIndex = fromIndex + direction;
  if (toIndex < 0 || toIndex >= next.length) return next;
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
}

function formatMoney(value) {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 0
  }).format(amount);
}

function formatDate(value) {
  if (!value) return 'Not set';
  try {
    return new Date(value).toLocaleString();
  } catch (_) {
    return String(value);
  }
}

function MarketplaceManagement({ apiWithAccessControl }) {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [resolved, setResolved] = useState({
    marketplaceUrl: '',
    sliderMerchants: [],
    featuredMerchants: [],
    featuredProducts: [],
    boostSummary: {
      activeCount: 0,
      queuedCount: 0,
      completedCount: 0,
      slots: 0,
      rotationHours: 0,
      queuePolicy: 'FIFO',
      visibleCampaignIds: []
    },
    boostCampaigns: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [merchantQuery, setMerchantQuery] = useState('');
  const [productQuery, setProductQuery] = useState('');
  const [merchantResults, setMerchantResults] = useState([]);
  const [productResults, setProductResults] = useState([]);
  const [searching, setSearching] = useState({ merchants: false, products: false });
  const [composer, setComposer] = useState(DEFAULT_COMPOSER);
  const [submittingCampaign, setSubmittingCampaign] = useState(false);
  const [campaignActionId, setCampaignActionId] = useState('');

  const selectedMerchantSlugs = useMemo(() => uniqueStrings([
    ...config.sliderMerchantSlugs,
    ...config.featuredMerchantSlugs
  ]), [config]);

  useEffect(() => {
    loadConfig();
  }, []);

  const hydratePayload = (data) => {
    setConfig({ ...DEFAULT_CONFIG, ...data.config });
    setResolved(data.resolved || {
      marketplaceUrl: '',
      sliderMerchants: [],
      featuredMerchants: [],
      featuredProducts: [],
      boostSummary: {
        activeCount: 0,
        queuedCount: 0,
        completedCount: 0,
        slots: 0,
        rotationHours: 0,
        queuePolicy: 'FIFO',
        visibleCampaignIds: []
      },
      boostCampaigns: []
    });
  };

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await apiWithAccessControl('/admin/marketplace/config');
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to load marketplace config');
      }
      hydratePayload(data);
      setComposer((current) => ({
        ...current,
        packageName: data.config?.sponsoredProducts?.defaultPackage?.name || DEFAULT_COMPOSER.packageName,
        durationDays: data.config?.sponsoredProducts?.defaultPackage?.durationDays || DEFAULT_COMPOSER.durationDays,
        price: data.config?.sponsoredProducts?.defaultPackage?.price || DEFAULT_COMPOSER.price
      }));
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Failed to load marketplace config' });
    } finally {
      setLoading(false);
    }
  };

  const updateHeroField = (field, value) => {
    setConfig((current) => ({
      ...current,
      hero: {
        ...current.hero,
        [field]: value
      }
    }));
  };

  const updateSectionField = (field, value) => {
    setConfig((current) => ({
      ...current,
      sections: {
        ...current.sections,
        [field]: value
      }
    }));
  };

  const updateTierLimit = (tier, value) => {
    setConfig((current) => ({
      ...current,
      tierProductLimits: {
        ...current.tierProductLimits,
        [tier]: Number(value || 0)
      }
    }));
  };

  const updateSponsoredField = (field, value) => {
    setConfig((current) => ({
      ...current,
      sponsoredProducts: {
        ...current.sponsoredProducts,
        [field]: value
      }
    }));
  };

  const updateTierPricing = (tier, field, value) => {
    setConfig((current) => ({
      ...current,
      sponsoredProducts: {
        ...current.sponsoredProducts,
        tierPricing: {
          ...current.sponsoredProducts.tierPricing,
          [tier]: {
            ...(current.sponsoredProducts.tierPricing?.[tier] || {}),
            [field]: Number(value || 0)
          }
        }
      }
    }));
  };

  const updateDefaultPackage = (field, value) => {
    setConfig((current) => ({
      ...current,
      sponsoredProducts: {
        ...current.sponsoredProducts,
        defaultPackage: {
          ...current.sponsoredProducts.defaultPackage,
          [field]: value
        }
      }
    }));
  };

  const runSearch = async (type) => {
    const query = type === 'merchants' ? merchantQuery : productQuery;
    if (!query.trim()) return;

    setSearching((current) => ({ ...current, [type]: true }));
    try {
      const response = await apiWithAccessControl(`/admin/marketplace/search/${type}?q=${encodeURIComponent(query.trim())}`);
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || `Failed to search ${type}`);
      }
      if (type === 'merchants') {
        setMerchantResults(data.merchants || []);
      } else {
        setProductResults(data.products || []);
      }
    } catch (error) {
      setStatus({ type: 'error', message: error.message || `Failed to search ${type}` });
    } finally {
      setSearching((current) => ({ ...current, [type]: false }));
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const response = await apiWithAccessControl('/admin/marketplace/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to save marketplace config');
      }
      hydratePayload(data);
      setStatus({ type: 'success', message: 'Marketplace config saved' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Failed to save marketplace config' });
    } finally {
      setSaving(false);
    }
  };

  const addMerchantSlug = (slug, target) => {
    setConfig((current) => ({
      ...current,
      [target]: uniqueStrings([...(current[target] || []), slug])
    }));
  };

  const removeMerchantSlug = (slug, target) => {
    setConfig((current) => ({
      ...current,
      [target]: (current[target] || []).filter((item) => item !== slug)
    }));
  };

  const addProductId = (id) => {
    setConfig((current) => ({
      ...current,
      featuredProductIds: uniqueStrings([...(current.featuredProductIds || []), id])
    }));
  };

  const removeProductId = (id) => {
    setConfig((current) => ({
      ...current,
      featuredProductIds: (current.featuredProductIds || []).filter((item) => item !== id)
    }));
  };

  const moveMerchantSlug = (slug, target, direction) => {
    setConfig((current) => {
      const currentItems = current[target] || [];
      const index = currentItems.indexOf(slug);
      if (index === -1) return current;
      return {
        ...current,
        [target]: moveArrayItem(currentItems, index, direction)
      };
    });
  };

  const moveProductId = (id, direction) => {
    setConfig((current) => {
      const currentItems = current.featuredProductIds || [];
      const index = currentItems.indexOf(id);
      if (index === -1) return current;
      return {
        ...current,
        featuredProductIds: moveArrayItem(currentItems, index, direction)
      };
    });
  };

  const createBoostCampaign = async () => {
    if (!composer.productId) {
      setStatus({ type: 'error', message: 'Choose a product to queue or activate a boost' });
      return;
    }

    setSubmittingCampaign(true);
    try {
      const response = await apiWithAccessControl('/admin/marketplace/boost-campaigns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(composer)
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to create boost campaign');
      }
      hydratePayload(data);
      setComposer({
        ...DEFAULT_COMPOSER,
        packageName: config.sponsoredProducts?.defaultPackage?.name || DEFAULT_COMPOSER.packageName,
        durationDays: config.sponsoredProducts?.defaultPackage?.durationDays || DEFAULT_COMPOSER.durationDays,
        price: config.sponsoredProducts?.defaultPackage?.price || DEFAULT_COMPOSER.price
      });
      setStatus({ type: 'success', message: 'Boost campaign created' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Failed to create boost campaign' });
    } finally {
      setSubmittingCampaign(false);
    }
  };

  const runCampaignAction = async (campaignId, action, body = {}) => {
    setCampaignActionId(`${campaignId}:${action}`);
    try {
      const response = await apiWithAccessControl(`/admin/marketplace/boost-campaigns/${campaignId}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || `Failed to ${action} boost campaign`);
      }
      hydratePayload(data);
      setStatus({ type: 'success', message: `Boost campaign ${action}d` });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || `Failed to ${action} boost campaign` });
    } finally {
      setCampaignActionId('');
    }
  };

  if (loading) {
    return (
      <div className="mkt-marketplace">
        <div className="mkt-marketplace-card">
          <p>Loading marketplace controls…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mkt-marketplace">
      <section className="mkt-marketplace-hero">
        <div>
          <div className="mkt-marketplace-badge">
            <Globe size={15} />
            Marketplace controls
          </div>
          <h1>Public marketplace</h1>
          <p>Merchant discovery stays public. Product visibility on the marketplace is now sponsored-first, with rotation, fallback rules, and transparent reporting.</p>
        </div>
        {resolved.marketplaceUrl ? (
          <a className="mkt-marketplace-preview" href={resolved.marketplaceUrl} target="_blank" rel="noreferrer">
            Open public marketplace
          </a>
        ) : null}
      </section>

      {status ? (
        <div className={`mkt-marketplace-status mkt-marketplace-status--${status.type}`}>
          {status.message}
        </div>
      ) : null}

      <div className="mkt-marketplace-grid">
        <section className="mkt-marketplace-card">
          <h2>Marketplace setup</h2>
          <p>Hero copy, merchant discovery controls, and tier product limits for store preview pages.</p>
          <div className="mkt-marketplace-form">
            <div className="mkt-marketplace-switches">
              <label className="mkt-marketplace-switch">
                <input
                  type="checkbox"
                  checked={Boolean(config.isEnabled)}
                  onChange={(event) => setConfig((current) => ({ ...current, isEnabled: event.target.checked }))}
                />
                <span>Marketplace enabled</span>
              </label>
              <label className="mkt-marketplace-switch">
                <input
                  type="checkbox"
                  checked={Boolean(config.sections?.featuredMerchants)}
                  onChange={(event) => updateSectionField('featuredMerchants', event.target.checked)}
                />
                <span>Show featured merchants section</span>
              </label>
              <label className="mkt-marketplace-switch">
                <input
                  type="checkbox"
                  checked={Boolean(config.sections?.allMerchants)}
                  onChange={(event) => updateSectionField('allMerchants', event.target.checked)}
                />
                <span>Show more merchants section</span>
              </label>
              <label className="mkt-marketplace-switch">
                <input
                  type="checkbox"
                  checked={Boolean(config.sections?.featuredProducts)}
                  onChange={(event) => updateSectionField('featuredProducts', event.target.checked)}
                />
                <span>Show product showcase section</span>
              </label>
            </div>

            <div className="mkt-marketplace-field-grid">
              <label className="mkt-marketplace-label">
                Eyebrow
                <input value={config.hero?.eyebrow || ''} onChange={(event) => updateHeroField('eyebrow', event.target.value)} />
              </label>
              <label className="mkt-marketplace-label">
                Hero title
                <input value={config.hero?.title || ''} onChange={(event) => updateHeroField('title', event.target.value)} />
              </label>
            </div>

            <label className="mkt-marketplace-label">
              Hero description
              <textarea value={config.hero?.description || ''} onChange={(event) => updateHeroField('description', event.target.value)} />
            </label>

            <div className="mkt-marketplace-field-grid">
              <label className="mkt-marketplace-label">
                Primary CTA label
                <input value={config.hero?.primaryCtaLabel || ''} onChange={(event) => updateHeroField('primaryCtaLabel', event.target.value)} />
              </label>
              <label className="mkt-marketplace-label">
                Secondary CTA label
                <input value={config.hero?.secondaryCtaLabel || ''} onChange={(event) => updateHeroField('secondaryCtaLabel', event.target.value)} />
              </label>
            </div>

            <div className="mkt-marketplace-field-grid">
              <label className="mkt-marketplace-label">
                Default products per merchant
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={config.merchantProductLimit}
                  onChange={(event) => setConfig((current) => ({ ...current, merchantProductLimit: Number(event.target.value || 1) }))}
                />
              </label>
              <label className="mkt-marketplace-label">
                Free tier product cap
                <input type="number" min="1" max="5" value={config.tierProductLimits?.FREE || 4} onChange={(event) => updateTierLimit('FREE', event.target.value)} />
              </label>
              <label className="mkt-marketplace-label">
                Premium tier product cap
                <input type="number" min="1" max="5" value={config.tierProductLimits?.PREMIUM || 5} onChange={(event) => updateTierLimit('PREMIUM', event.target.value)} />
              </label>
              <label className="mkt-marketplace-label">
                Pro tier product cap
                <input type="number" min="1" max="5" value={config.tierProductLimits?.PRO || 5} onChange={(event) => updateTierLimit('PRO', event.target.value)} />
              </label>
              <label className="mkt-marketplace-label">
                Pro Plus tier product cap
                <input type="number" min="1" max="5" value={config.tierProductLimits?.['PRO PLUS'] || 5} onChange={(event) => updateTierLimit('PRO PLUS', event.target.value)} />
              </label>
            </div>

            <div className="mkt-marketplace-actions">
              <button className="mkt-marketplace-btn" onClick={saveConfig} disabled={saving}>
                {saving ? 'Saving…' : 'Save marketplace'}
              </button>
              <button className="mkt-marketplace-btn mkt-marketplace-btn--secondary" onClick={loadConfig} disabled={saving}>
                Reload
              </button>
            </div>
          </div>
        </section>

        <section className="mkt-marketplace-card">
          <h2>Featured merchants</h2>
          <p>Merchants stay visible on the marketplace even when product boosts are sold separately. Slider stores appear in the moving strip. Featured stores get the main merchant cards.</p>

          <div className="mkt-marketplace-search">
            <div className="mkt-marketplace-search-row">
              <input
                value={merchantQuery}
                onChange={(event) => setMerchantQuery(event.target.value)}
                placeholder="Search live merchants by name, slug, or email"
              />
              <button className="mkt-marketplace-inline-btn" onClick={() => runSearch('merchants')} disabled={searching.merchants}>
                {searching.merchants ? 'Searching…' : 'Search'}
              </button>
            </div>

            <div className="mkt-marketplace-results">
              {merchantResults.map((merchant) => (
                <div key={merchant.slug} className="mkt-marketplace-result">
                  <div>
                    <strong>{merchant.name}</strong>
                    <span>{merchant.slug} · {merchant.merchantTier}</span>
                  </div>
                  <div className="mkt-marketplace-actions">
                    <button className="mkt-marketplace-inline-btn" onClick={() => addMerchantSlug(merchant.slug, 'sliderMerchantSlugs')}>Add to slider</button>
                    <button className="mkt-marketplace-inline-btn" onClick={() => addMerchantSlug(merchant.slug, 'featuredMerchantSlugs')}>Feature</button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mkt-marketplace-selected">
              {resolved.sliderMerchants.map((merchant, index) => (
                <div key={`slider-${merchant.slug}`} className="mkt-marketplace-chip">
                  <div>
                    <strong>{merchant.name}</strong>
                    <span>Slider slot {index + 1} · {merchant.slug} · {merchant.merchantTier || 'FREE'}</span>
                  </div>
                  <div className="mkt-marketplace-actions">
                    <button className="mkt-marketplace-inline-btn" onClick={() => moveMerchantSlug(merchant.slug, 'sliderMerchantSlugs', -1)} disabled={index === 0}>Up</button>
                    <button className="mkt-marketplace-inline-btn" onClick={() => moveMerchantSlug(merchant.slug, 'sliderMerchantSlugs', 1)} disabled={index === resolved.sliderMerchants.length - 1}>Down</button>
                    <button className="mkt-marketplace-inline-btn" onClick={() => removeMerchantSlug(merchant.slug, 'sliderMerchantSlugs')}>Remove</button>
                  </div>
                </div>
              ))}
              {resolved.featuredMerchants.map((merchant, index) => (
                <div key={`featured-${merchant.slug}`} className="mkt-marketplace-chip">
                  <div>
                    <strong>{merchant.name}</strong>
                    <span>Featured slot {index + 1} · {merchant.slug} · {merchant.merchantTier || 'FREE'}</span>
                  </div>
                  <div className="mkt-marketplace-actions">
                    <button className="mkt-marketplace-inline-btn" onClick={() => moveMerchantSlug(merchant.slug, 'featuredMerchantSlugs', -1)} disabled={index === 0}>Up</button>
                    <button className="mkt-marketplace-inline-btn" onClick={() => moveMerchantSlug(merchant.slug, 'featuredMerchantSlugs', 1)} disabled={index === resolved.featuredMerchants.length - 1}>Down</button>
                    <button className="mkt-marketplace-inline-btn" onClick={() => removeMerchantSlug(merchant.slug, 'featuredMerchantSlugs')}>Remove</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      <section className="mkt-marketplace-card">
        <h2>Sponsored product setup</h2>
        <p>Marketplace product slots are now paid/sponsored-first. Configure how long campaigns rotate, how many run at once, whether fallback products should appear, and what merchants should expect in their reports.</p>
        <div className="mkt-marketplace-form">
          <div className="mkt-marketplace-switches">
            <label className="mkt-marketplace-switch">
              <input
                type="checkbox"
                checked={Boolean(config.sponsoredProducts?.enabled)}
                onChange={(event) => updateSponsoredField('enabled', event.target.checked)}
              />
              <span>Sponsored product system enabled</span>
            </label>
            <label className="mkt-marketplace-switch">
              <input
                type="checkbox"
                checked={Boolean(config.sponsoredProducts?.fallbackToFeaturedProducts)}
                onChange={(event) => updateSponsoredField('fallbackToFeaturedProducts', event.target.checked)}
              />
              <span>Fallback to curated products when no boost is live</span>
            </label>
            <label className="mkt-marketplace-switch">
              <input
                type="checkbox"
                checked={Boolean(config.sponsoredProducts?.directoryOnlyWithoutBoosts)}
                onChange={(event) => updateSponsoredField('directoryOnlyWithoutBoosts', event.target.checked)}
              />
              <span>Hide marketplace products entirely when there are no live boosts</span>
            </label>
          </div>

          <div className="mkt-marketplace-field-grid">
            <label className="mkt-marketplace-label">
              Visible sponsored slots
              <input type="number" min="1" max="12" value={config.sponsoredProducts?.slots || 6} onChange={(event) => updateSponsoredField('slots', Number(event.target.value || 1))} />
            </label>
            <label className="mkt-marketplace-label">
              Rotation window (hours)
              <input type="number" min="1" max="168" value={config.sponsoredProducts?.rotationHours || 6} onChange={(event) => updateSponsoredField('rotationHours', Number(event.target.value || 1))} />
            </label>
            <label className="mkt-marketplace-label">
              Queue policy
              <select value={config.sponsoredProducts?.queuePolicy || 'FIFO'} onChange={(event) => updateSponsoredField('queuePolicy', event.target.value)}>
                <option value="FIFO">FIFO</option>
                <option value="MANUAL">Manual</option>
              </select>
            </label>
          </div>

          <div className="mkt-marketplace-field-grid">
            <label className="mkt-marketplace-label">
              Default package name
              <input value={config.sponsoredProducts?.defaultPackage?.name || ''} onChange={(event) => updateDefaultPackage('name', event.target.value)} />
            </label>
            <label className="mkt-marketplace-label">
              Default duration (days)
              <input type="number" min="1" max="30" value={config.sponsoredProducts?.defaultPackage?.durationDays || 7} onChange={(event) => updateDefaultPackage('durationDays', Number(event.target.value || 1))} />
            </label>
            <label className="mkt-marketplace-label">
              Default price
              <input type="number" min="0" value={config.sponsoredProducts?.defaultPackage?.price || 0} onChange={(event) => updateDefaultPackage('price', Number(event.target.value || 0))} />
            </label>
          </div>

          <label className="mkt-marketplace-label">
            Merchant report note
            <textarea value={config.sponsoredProducts?.reportNote || ''} onChange={(event) => updateSponsoredField('reportNote', event.target.value)} />
          </label>

          <div className="mkt-marketplace-field-grid">
            {['FREE', 'PREMIUM', 'PRO', 'PRO PLUS'].map((tier) => (
              <div key={tier} className="mkt-marketplace-tier-card">
                <strong>{tier}</strong>
                <label className="mkt-marketplace-label">
                  Discount %
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={config.sponsoredProducts?.tierPricing?.[tier]?.discountPercent || 0}
                    onChange={(event) => updateTierPricing(tier, 'discountPercent', event.target.value)}
                  />
                </label>
                <label className="mkt-marketplace-label">
                  Priority weight
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={config.sponsoredProducts?.tierPricing?.[tier]?.priorityWeight || 1}
                    onChange={(event) => updateTierPricing(tier, 'priorityWeight', event.target.value)}
                  />
                </label>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mkt-marketplace-card">
        <h2>Curated fallback products</h2>
        <p>These only appear when no paid boost is live and fallback is enabled. Keep this list tight so sponsored products stay valuable.</p>
        <div className="mkt-marketplace-search">
          <div className="mkt-marketplace-search-row">
            <input
              value={productQuery}
              onChange={(event) => setProductQuery(event.target.value)}
              placeholder="Search live products by name, slug, or SKU"
            />
            <button className="mkt-marketplace-inline-btn" onClick={() => runSearch('products')} disabled={searching.products}>
              {searching.products ? 'Searching…' : 'Search'}
            </button>
          </div>
          <div className="mkt-marketplace-results">
            {productResults.map((product) => (
              <div key={product.id} className="mkt-marketplace-result">
                <div>
                  <strong>{product.name}</strong>
                  <span>{product.storeName} · {product.id}</span>
                </div>
                <div className="mkt-marketplace-actions">
                  <button className="mkt-marketplace-inline-btn" onClick={() => addProductId(product.id)}>Add fallback</button>
                  <button
                    className="mkt-marketplace-inline-btn"
                    onClick={() => setComposer((current) => ({
                      ...current,
                      productId: product.id
                    }))}
                  >
                    Use for boost
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="mkt-marketplace-selected">
            {resolved.featuredProducts.map((product, index) => (
              <div key={product.id} className="mkt-marketplace-chip">
                <div>
                  <strong>{product.name}</strong>
                  <span>Fallback slot {index + 1} · {product.storeName || 'Unknown store'} · {product.id}</span>
                </div>
                <div className="mkt-marketplace-actions">
                  <button className="mkt-marketplace-inline-btn" onClick={() => moveProductId(product.id, -1)} disabled={index === 0}>Up</button>
                  <button className="mkt-marketplace-inline-btn" onClick={() => moveProductId(product.id, 1)} disabled={index === resolved.featuredProducts.length - 1}>Down</button>
                  <button className="mkt-marketplace-inline-btn" onClick={() => removeProductId(product.id)}>Remove</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mkt-marketplace-card">
        <h2>Boost queue and reporting</h2>
        <p>Sell boost as a separate add-on. Queue the campaign, activate it when payment is cleared, and use this report to show transparency on duration, rotation, and actual marketplace exposure.</p>

        <div className="mkt-marketplace-field-grid">
          <div className="mkt-marketplace-summary-card">
            <strong>Active boosts</strong>
            <span>{resolved.boostSummary?.activeCount || 0}</span>
            <small>{resolved.boostSummary?.slots || 0} visible slots · rotates every {resolved.boostSummary?.rotationHours || 0}h</small>
          </div>
          <div className="mkt-marketplace-summary-card">
            <strong>Queued boosts</strong>
            <span>{resolved.boostSummary?.queuedCount || 0}</span>
            <small>Queue policy: {resolved.boostSummary?.queuePolicy || 'FIFO'}</small>
          </div>
          <div className="mkt-marketplace-summary-card">
            <strong>Completed boosts</strong>
            <span>{resolved.boostSummary?.completedCount || 0}</span>
            <small>Transparent reporting is enabled through marketplace events</small>
          </div>
        </div>

        <div className="mkt-marketplace-card mkt-marketplace-card--soft">
          <h3>Queue a new boost</h3>
          <div className="mkt-marketplace-form">
            <div className="mkt-marketplace-field-grid">
              <label className="mkt-marketplace-label">
                Product ID
                <input value={composer.productId} onChange={(event) => setComposer((current) => ({ ...current, productId: event.target.value }))} placeholder="Pick from search results above" />
              </label>
              <label className="mkt-marketplace-label">
                Package name
                <input value={composer.packageName} onChange={(event) => setComposer((current) => ({ ...current, packageName: event.target.value }))} />
              </label>
              <label className="mkt-marketplace-label">
                Duration (days)
                <input type="number" min="1" max="30" value={composer.durationDays} onChange={(event) => setComposer((current) => ({ ...current, durationDays: Number(event.target.value || 1) }))} />
              </label>
              <label className="mkt-marketplace-label">
                Price charged
                <input type="number" min="0" value={composer.price} onChange={(event) => setComposer((current) => ({ ...current, price: Number(event.target.value || 0) }))} />
              </label>
              <label className="mkt-marketplace-label">
                Start mode
                <select value={composer.startMode} onChange={(event) => setComposer((current) => ({ ...current, startMode: event.target.value }))}>
                  <option value="queue">Queue first</option>
                  <option value="active">Activate immediately</option>
                </select>
              </label>
            </div>
            <label className="mkt-marketplace-label">
              Notes to merchant / internal ops note
              <textarea value={composer.notes} onChange={(event) => setComposer((current) => ({ ...current, notes: event.target.value }))} />
            </label>
            <div className="mkt-marketplace-actions">
              <button className="mkt-marketplace-btn" onClick={createBoostCampaign} disabled={submittingCampaign}>
                {submittingCampaign ? 'Creating…' : 'Create boost campaign'}
              </button>
            </div>
          </div>
        </div>

        <div className="mkt-marketplace-selected">
          {resolved.boostCampaigns?.map((campaign) => {
            const busyPrefix = `${campaign.id}:`;
            const isBusy = campaignActionId.startsWith(busyPrefix);
            const isVisibleNow = (resolved.boostSummary?.visibleCampaignIds || []).includes(campaign.id);
            return (
              <div key={campaign.id} className="mkt-marketplace-chip mkt-marketplace-chip--campaign">
                <div>
                  <strong>{campaign.product?.name || campaign.product?.id}</strong>
                  <span>{campaign.product?.storeName || 'Unknown store'} · {campaign.packageName} · {campaign.status}</span>
                  <span>Duration: {campaign.durationDays} day(s) · Price: {formatMoney(campaign.price)}</span>
                  <span>Queued: {formatDate(campaign.queuedAt)} · Starts: {formatDate(campaign.startAt)} · Ends: {formatDate(campaign.endAt)}</span>
                  <span>Impressions: {campaign.metrics?.impressions || 0} · Clicks: {campaign.metrics?.clicks || 0} · Product views: {campaign.metrics?.productViews || 0} · CTR: {campaign.metrics?.ctr || 0}%</span>
                  <span>{isVisibleNow ? 'Currently occupying a sponsored slot in rotation.' : config.sponsoredProducts?.reportNote}</span>
                </div>
                <div className="mkt-marketplace-actions">
                  <button className="mkt-marketplace-inline-btn" onClick={() => runCampaignAction(campaign.id, 'move', { direction: 'up' })} disabled={isBusy}>Up</button>
                  <button className="mkt-marketplace-inline-btn" onClick={() => runCampaignAction(campaign.id, 'move', { direction: 'down' })} disabled={isBusy}>Down</button>
                  {campaign.status !== 'ACTIVE' ? (
                    <button className="mkt-marketplace-inline-btn" onClick={() => runCampaignAction(campaign.id, 'activate', { durationDays: campaign.durationDays })} disabled={isBusy}>
                      Activate
                    </button>
                  ) : null}
                  {campaign.status === 'ACTIVE' ? (
                    <button className="mkt-marketplace-inline-btn" onClick={() => runCampaignAction(campaign.id, 'complete')} disabled={isBusy}>
                      Complete
                    </button>
                  ) : null}
                  {campaign.status !== 'CANCELLED' && campaign.status !== 'COMPLETED' ? (
                    <button className="mkt-marketplace-inline-btn" onClick={() => runCampaignAction(campaign.id, 'cancel')} disabled={isBusy}>
                      Cancel
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

export default MarketplaceManagement;
